/**
 * Icon Picker Component for Puck Editor
 *
 * Allows users to select from a curated list of Lucide icons
 */

import React, { useState, useRef, useEffect } from "react";
import * as Icons from "lucide-react";
import { FieldLabel } from "@puckeditor/core";

interface IconPickerProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
}

// Curated list of commonly used service icons
const COMMON_ICONS = [
  { name: "Truck", label: "Truck" },
  { name: "PackageCheck", label: "Package Check" },
  { name: "Trash2", label: "Trash" },
  { name: "Wrench", label: "Wrench" },
  { name: "Zap", label: "Lightning" },
  { name: "Home", label: "Home" },
  { name: "Wind", label: "Wind" },
  { name: "Flame", label: "Flame" },
  { name: "Snowflake", label: "Snowflake" },
  { name: "Droplet", label: "Droplet" },
  { name: "Lightbulb", label: "Lightbulb" },
  { name: "Settings", label: "Settings" },
  { name: "Tool", label: "Tool" },
  { name: "Shield", label: "Shield" },
  { name: "CheckCircle", label: "Check Circle" },
  { name: "Star", label: "Star" },
  { name: "Phone", label: "Phone" },
  { name: "Mail", label: "Mail" },
  { name: "MapPin", label: "Map Pin" },
  { name: "Calendar", label: "Calendar" },
  { name: "Clock", label: "Clock" },
  { name: "Users", label: "Users" },
  { name: "Heart", label: "Heart" },
  { name: "Award", label: "Award" },
  { name: "Briefcase", label: "Briefcase" },
  { name: "Car", label: "Car" },
  { name: "Wifi", label: "Wifi" },
  { name: "Lock", label: "Lock" },
  { name: "Key", label: "Key" },
  { name: "Hammer", label: "Hammer" },
];

export const IconPicker: React.FC<IconPickerProps> = ({ value = "", onChange, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [isOpen]);

  const renderIcon = (iconName: string) => {
    const IconComponent = (Icons as any)[iconName];
    if (!IconComponent) return null;
    return <IconComponent size={20} strokeWidth={1.5} />;
  };

  const selectedIcon = COMMON_ICONS.find((icon) => icon.name === value);

  return (
    <FieldLabel label={label ?? "Icon"} el="div">
      <div style={{ position: "relative" }}>
        {/* Selected Icon Display / Trigger Button */}
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          style={{
            width: "100%",
            padding: "8px 12px",
            fontSize: "14px",
            lineHeight: "1.5",
            color: "#0D1015",
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            borderRadius: "6px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {value && renderIcon(value)}
            <span>{selectedIcon?.label || "Select an icon"}</span>
          </div>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            style={{
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
            }}
          >
            <path
              d="M4 6L8 10L12 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Dropdown Panel */}
        {isOpen && (
          <>
            {/* Backdrop */}
            <div
              onClick={() => setIsOpen(false)}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 999,
              }}
            />

            {/* Icon Grid */}
            <div
              style={{
                position: "fixed",
                top: `${dropdownPos.top}px`,
                left: `${dropdownPos.left}px`,
                backgroundColor: "#FFFFFF",
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                padding: "8px",
                zIndex: 1000,
                maxHeight: "300px",
                overflowY: "auto",
                width: `${dropdownPos.width}px`,
                minWidth: "280px",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: "4px",
                }}
              >
                {COMMON_ICONS.map((icon) => (
                  <button
                    key={icon.name}
                    type="button"
                    onClick={() => {
                      onChange(icon.name);
                      setIsOpen(false);
                    }}
                    title={icon.label}
                    style={{
                      padding: "12px",
                      border: "1px solid",
                      borderColor: value === icon.name ? "#6366F1" : "#E5E7EB",
                      borderRadius: "6px",
                      backgroundColor: value === icon.name ? "#EEF2FF" : "#FFFFFF",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.15s ease",
                      color: value === icon.name ? "#6366F1" : "#374151",
                    }}
                    onMouseEnter={(e) => {
                      if (value !== icon.name) {
                        e.currentTarget.style.backgroundColor = "#F9FAFB";
                        e.currentTarget.style.borderColor = "#D1D5DB";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (value !== icon.name) {
                        e.currentTarget.style.backgroundColor = "#FFFFFF";
                        e.currentTarget.style.borderColor = "#E5E7EB";
                      }
                    }}
                  >
                    {renderIcon(icon.name)}
                  </button>
                ))}
              </div>

              {/* Clear Selection Button */}
              {value && (
                <button
                  type="button"
                  onClick={() => {
                    onChange("");
                    setIsOpen(false);
                  }}
                  style={{
                    width: "100%",
                    marginTop: "8px",
                    padding: "8px",
                    fontSize: "13px",
                    color: "#DC2626",
                    backgroundColor: "#FEF2F2",
                    border: "1px solid #FEE2E2",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  Clear Selection
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </FieldLabel>
  );
};
